/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import {Logger} from '@app/lib/Logger';
import {ExternalUrls} from '@fluxer/constants/src/ExternalUrls';
import {makeAutoObservable, runInAction} from 'mobx';

type IncidentStatus = 'investigating' | 'identified' | 'monitoring' | 'resolved';
type IncidentImpact = 'critical' | 'major' | 'minor' | 'maintenance';
type MaintenanceStatus = 'scheduled' | 'in_progress' | 'completed';

export interface StatusPageIncident {
	id: string;
	name: string;
	status: IncidentStatus;
	impact: IncidentImpact;
	url: string;
	updatedAt: string;
}

export interface StatusPageMaintenance {
	id: string;
	name: string;
	status: MaintenanceStatus;
	scheduledFor: string;
	scheduledUntil: string;
	url: string;
}

const INCIDENT_STATUS_MAP: Record<string, IncidentStatus> = {
	INVESTIGATING: 'investigating',
	IDENTIFIED: 'identified',
	MONITORING: 'monitoring',
	RESOLVED: 'resolved',
};

const INCIDENT_IMPACT_MAP: Record<string, IncidentImpact> = {
	MAJOROUTAGE: 'critical',
	PARTIALOUTAGE: 'major',
	DEGRADEDPERFORMANCE: 'minor',
	UNDERMAINTENANCE: 'maintenance',
};

const MAINTENANCE_STATUS_MAP: Record<string, MaintenanceStatus> = {
	NOTSTARTEDYET: 'scheduled',
	INPROGRESS: 'in_progress',
	COMPLETED: 'completed',
};

interface InstatusIncident {
	id: string;
	name: string;
	status: string;
	impact: string;
	url: string;
	updated: string;
	resolved: string | null;
}

interface InstatusMaintenance {
	id: string;
	name: string;
	status: string;
	url: string;
	scheduledFor: string;
	scheduledUntil: string;
}

interface InstatusSummary {
	activeIncidents: Array<InstatusIncident>;
	activeMaintenances: Array<InstatusMaintenance>;
}

const logger = new Logger('StatusPageStore');

const POLL_BASE_INTERVAL_MS = 5 * 60 * 1000;
const POLL_JITTER_MS = 60 * 1000;

function computePollDelay(): number {
	return POLL_BASE_INTERVAL_MS + Math.random() * POLL_JITTER_MS;
}

class StatusPageStore {
	incident: StatusPageIncident | null = null;
	scheduledMaintenance: StatusPageMaintenance | null = null;

	pollTimerId: ReturnType<typeof setTimeout> | null = null;

	constructor() {
		makeAutoObservable(this, {pollTimerId: false}, {autoBind: true});
	}

	startPolling(): void {
		if (this.pollTimerId !== null) {
			return;
		}
		this.schedulePoll();
	}

	stopPolling(): void {
		if (this.pollTimerId !== null) {
			clearTimeout(this.pollTimerId);
			this.pollTimerId = null;
		}
	}

	private schedulePoll(): void {
		this.pollTimerId = setTimeout(() => {
			if (document.visibilityState === 'visible') {
				void this.checkIncidents().finally(() => this.schedulePoll());
			} else {
				this.schedulePoll();
			}
		}, computePollDelay());
	}

	async checkIncidents(): Promise<void> {
		try {
			const response = await fetch(`${ExternalUrls.SERVICE_STATUS}/summary.json`);
			if (!response.ok) {
				runInAction(() => {
					this.incident = null;
					this.scheduledMaintenance = null;
				});
				return;
			}

			const data: InstatusSummary = await response.json();

			const activeIncident = data.activeIncidents?.find((inc) => inc.status !== 'RESOLVED' && inc.resolved == null);

			const activeMaintenance = data.activeMaintenances?.find((maint) => maint.status === 'INPROGRESS');

			runInAction(() => {
				if (activeIncident) {
					this.incident = {
						id: activeIncident.id,
						name: activeIncident.name,
						status: INCIDENT_STATUS_MAP[activeIncident.status] ?? 'investigating',
						impact: INCIDENT_IMPACT_MAP[activeIncident.impact] ?? 'minor',
						url: activeIncident.url,
						updatedAt: activeIncident.updated,
					};
				} else if (activeMaintenance) {
					this.incident = {
						id: activeMaintenance.id,
						name: activeMaintenance.name,
						status: 'investigating',
						impact: 'maintenance',
						url: activeMaintenance.url,
						updatedAt: '',
					};
				} else {
					this.incident = null;
				}
			});

			this.checkScheduledMaintenances(data);
		} catch {
			logger.warn('Failed to fetch status page');
			runInAction(() => {
				this.incident = null;
				this.scheduledMaintenance = null;
			});
		}
	}

	private checkScheduledMaintenances(data: InstatusSummary): void {
		const scheduled = data.activeMaintenances?.find((maint) => maint.status === 'NOTSTARTEDYET');

		runInAction(() => {
			if (scheduled) {
				this.scheduledMaintenance = {
					id: scheduled.id,
					name: scheduled.name,
					status: MAINTENANCE_STATUS_MAP[scheduled.status] ?? 'scheduled',
					scheduledFor: scheduled.scheduledFor,
					scheduledUntil: scheduled.scheduledUntil,
					url: scheduled.url,
				};
			} else {
				this.scheduledMaintenance = null;
			}
		});
	}

	clearIncident(): void {
		this.incident = null;
	}
}

export default new StatusPageStore();
